/** @format */

import React, {useEffect, useMemo, useState} from 'react';

import * as R from 'ramda';
import Alert from '@mui/material/Alert';
import clsx from 'clsx';
import {Box, Grid, LinearProgress, Link, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import TuneIcon from '@mui/icons-material/Tune';

import AdTypePlatforms from 'components/icons/AdTypePlatforms';
import Breadcrumbs from 'components/Breadcrumbs';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import SecondaryMediumButton from 'components/buttons/SecondaryMediumButton';
import SecondarySelect from 'components/selects/SecondarySelect';

import {platformsFromAdType} from 'helpers/adTypeHelpers';
import {useSearchParams} from 'helpers/routerHelpers';
import {isGroupsMode} from 'helpers/sensorGroupsHelpers';

import {Mutable} from 'types';

import {
  AccountsAdTypeSensorsInput,
  AccountsNotificationChannelType,
  AccountsNotificationCreateInput,
  AccountsNotificationUpdateInput,
  YalperChecksSensorGroup,
} from 'graphql/generated/types';

import SensorsModal from './SensorsModal';
import RadioRow from './RadioRow';
import Deleted from './Deleted';
import {SensorGroupsSelector} from './SensorGroupsSelector';
import {useStyles} from './styles';
import {
  useCreateNotificationNewMutation,
  useDeleteNotificationMutation,
  useGetCurrentUserFormNewQuery,
  useUpdateNotificationNewMutation,
} from '../graphql/__generated__';
import {
  AdTypeFromQuery,
  AdTypeSelectedType,
  AdTypeSensorFromQuery,
  MonitorAdTypeFromQuery,
  SensorFromQuery,
} from '../types';

const DEFAULT_SELECTED_GROUPS: YalperChecksSensorGroup[] = ['RED', 'YELLOW', 'GRAY'];

const AD_TYPES: AdTypeFromQuery[] = ['CONTEXT', 'TARGET'];

export const NotificationFormNew = () => {
  const searchParams = useSearchParams();
  const specialistId = searchParams.get('specialistId');
  const id = searchParams.get('id');

  const {t} = useTranslation();
  const navigate = useNavigate();
  const classes = useStyles();

  const [companyId, setCompanyId] = useState<string>('');
  const [channelType, setChannelType] = useState<AccountsNotificationChannelType>('EMAIL');
  const [adTypesState, setAdTypesState] = useState<AdTypeFromQuery[]>([]);
  const [monitorAdTypesState, setMonitorAdTypesState] = useState<MonitorAdTypeFromQuery[]>([]);
  const [email, setEmail] = useState<string>('');
  const [deleted, setDeleted] = useState(false);
  const [sensorsModalsDispay, setSensorsModalDisplay] = useState<Record<AdTypeFromQuery, boolean>>({
    CONTEXT: false,
    TARGET: false,
  });
  const [adTypeSelectedSensors, setAdTypeSelectedSensors] = useState<AdTypeSelectedType>({
    CONTEXT: {},
    TARGET: {},
  });
  const [selectedGroups, setSelectedGroups] =
    useState<YalperChecksSensorGroup[]>(DEFAULT_SELECTED_GROUPS);

  const {loading, error, data} = useGetCurrentUserFormNewQuery({
    skip: !specialistId,
    variables: {specialistId: specialistId!, notificationId: id},
  });

  const [createNotificationNew, {loading: createNotificationNewLoading}] =
    useCreateNotificationNewMutation();
  const [updateNotificationNew, {loading: updateNotificationNewLoading}] =
    useUpdateNotificationNewMutation();
  const [deleteNotification, {loading: deleteNotificationLoading}] =
    useDeleteNotificationMutation();

  const notificationData = data?.currentUser?.notification;

  // currentSensorGroups: source of truth for the current row's groups.
  // Edit-mode → notification.company.sensorGroups (project is fixed).
  // Create-mode → companies.find(id === companyId).sensorGroups.
  const currentSensorGroups = useMemo(() => {
    if (notificationData) return notificationData.company.sensorGroups;
    return data?.currentUser?.companies.find(c => c.id === companyId)?.sensorGroups;
  }, [notificationData, data, companyId]);

  const groupsModeActive = isGroupsMode(currentSensorGroups);
  const isNewFlow = groupsModeActive;

  useEffect(() => {
    if (!data?.currentUser) {
      return;
    }

    if (data.currentUser.notification) {
      const {company, channelType, adTypes, monitorAdTypes, emailChannel, selectedSensorGroups} =
        data.currentUser.notification;

      setCompanyId(company.id);
      setChannelType(channelType);
      setAdTypesState([...adTypes]);
      setMonitorAdTypesState([...monitorAdTypes]);
      setEmail(emailChannel.email || '');

      // Init selectedGroups for edit-mode. Empty array (legacy record) → defaults to all three.
      if (selectedSensorGroups.length > 0) {
        setSelectedGroups([...selectedSensorGroups]);
      } else {
        setSelectedGroups([...DEFAULT_SELECTED_GROUPS]);
      }
    } else {
      const {companies} = data.currentUser;

      setCompanyId(companies[0]?.id || '');
      setChannelType('EMAIL');
      setAdTypesState([]);
      setMonitorAdTypesState([]);
      setEmail(data.currentUser.specialist?.email || '');
      // Reset selectedGroups to default for create-mode on initial data load.
      setSelectedGroups([...DEFAULT_SELECTED_GROUPS]);
    }

    const initialAdTypeSelectedSensors: AdTypeSelectedType = {
      CONTEXT: {},
      TARGET: {},
    };

    AD_TYPES.forEach(v => {
      const type = v as AdTypeFromQuery;
      const platforms = platformsFromAdType(type);
      let storedSensors: string[] = [];
      const key = type.toLowerCase() as keyof AdTypeSensorFromQuery;

      if (data.currentUser?.notification) {
        const sensors = data.currentUser.notification.adTypeSensors[key];
        if (Array.isArray(sensors)) {
          storedSensors = sensors;
        }
      }

      const isSensorChecked = (name: string) => {
        return storedSensors.length === 0 || storedSensors.includes(name);
      };

      if (data.currentUser?.sensors) {
        initialAdTypeSelectedSensors[type] = R.pipe(
          R.filter((s: SensorFromQuery) => platforms.includes(s.platform)),
          R.map(s => s.name),
          R.uniq,
          R.map(name => [name, isSensorChecked(name)] as [string, boolean]),
          R.fromPairs,
        )(data.currentUser.sensors);
      }
    });
    setAdTypeSelectedSensors(initialAdTypeSelectedSensors);
  }, [data]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (deleted) {
    return <Deleted email={email} />;
  }
  if (loading || !data?.currentUser) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const {notification, roles} = data.currentUser;

  const xeniooUserUuid = () => {
    if (data.currentUser?.notification) {
      return data.currentUser.notification.telegramChannel.xeniooUserUuid || '';
    } else {
      return data.currentUser?.specialist?.uuid;
    }
  };

  const renderChannelTypeSelect = () => {
    const handleSelect = (channelType: AccountsNotificationChannelType) => {
      const currentEmail = data.currentUser?.specialist?.email ?? '';
      setChannelType(channelType);
      setEmail(channelType === 'EMAIL' && currentEmail ? currentEmail : '');
    };

    const options = [
      {value: 'EMAIL', text: t('Email', 'Email')},
      {value: 'TELEGRAM', text: t('Telegram', 'Telegram')},
    ];

    return (
      <Box mt={2} className={classes.selectWrapper}>
        <Box className={classes.selectTitle}>
          <Trans>Выберите канал</Trans>
        </Box>

        <SecondarySelect
          selectClassName={classes.selectItem}
          disabled={Boolean(notification)}
          options={options}
          value={channelType}
          onSelect={handleSelect}
        />
      </Box>
    );
  };

  const renderCompanySelect = () => {
    if (!data.currentUser) {
      return;
    }

    const handleSelect = (companyId: string) => {
      setCompanyId(companyId);
      // Reset selectedGroups to default whenever the user switches project (create-mode).
      // This avoids "stuck" state when switching groups → legacy → groups.
      setSelectedGroups([...DEFAULT_SELECTED_GROUPS]);
    };

    const options = data.currentUser.companies.map(v => ({
      value: v.id,
      text: v.name,
    }));

    return (
      <Box mt={4} className={classes.selectWrapper}>
        <Box className={classes.selectTitle}>
          <Trans>Выберите проект</Trans>
        </Box>

        <SecondarySelect
          selectClassName={classes.selectItem}
          options={options}
          disabled={Boolean(notification)}
          value={companyId || ''}
          aria-label='company-select-input'
          onSelect={handleSelect}
        />
      </Box>
    );
  };

  const renderAdTypeRadios = () => {
    const selectedAdType = (adType: AdTypeFromQuery) => adTypesState.includes(adType);
    const selectedMonitorAdType = (adType: AdTypeFromQuery) => monitorAdTypesState.includes(adType);

    const handleSelectAdType = (adType: AdTypeFromQuery) => () => {
      const adTypes = R.includes(adType, adTypesState)
        ? R.without([adType], adTypesState)
        : R.concat(adTypesState, [adType]);

      setAdTypesState(adTypes);
    };

    const handleSelectMonitorAdType = (adType: AdTypeFromQuery) => () => {
      const adTypes = R.includes(adType, monitorAdTypesState)
        ? R.without([adType], monitorAdTypesState)
        : R.concat(monitorAdTypesState, [adType]);

      setMonitorAdTypesState(adTypes);
    };

    const handleShowModal = (adType: AdTypeFromQuery) => {
      const newState = {...sensorsModalsDispay};
      newState[adType] = true;
      setSensorsModalDisplay(newState);
    };

    const handleCloseModal = (adType: AdTypeFromQuery) => {
      const newState = {...sensorsModalsDispay};
      newState[adType] = false;
      setSensorsModalDisplay(newState);
    };

    const handleSubmit = (adType: AdTypeFromQuery, newValue: Record<string, boolean>) => {
      setAdTypeSelectedSensors(prev => {
        const newState: AdTypeSelectedType = {...prev};
        R.toPairs(newValue).forEach(([k, v]) => {
          newState[adType][k] = v;
        });
        return newState;
      });
      handleCloseModal(adType);
    };

    const text = (v: AdTypeFromQuery) => {
      if (v === 'CONTEXT') return t('Контекст', 'Контекст');
      if (v === 'TARGET') return t('Таргет', 'Таргет');
      return null;
    };

    return (
      <Box mt={4}>
        {isNewFlow && currentSensorGroups && (
          <Box mb={4}>
            <SensorGroupsSelector
              sensorGroups={currentSensorGroups}
              value={selectedGroups}
              onChange={setSelectedGroups}
            />
          </Box>
        )}
        {!isNewFlow && (
          <Grid container spacing={2}>
            {AD_TYPES.map((v, i) => (
              <React.Fragment key={i}>
                <Grid item xs={12}>
                  <Box className={classes.adTypesRow} display={'flex'} flexWrap={'wrap'}>
                    <RadioRow
                      selected={selectedAdType(v as AdTypeFromQuery)}
                      onSelect={handleSelectAdType(v)}
                    >
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        className={classes.adTypesContainer}
                        flexWrap='wrap'
                      >
                        <Box display='flex' justifyContent='space-between' flexGrow={1}>
                          <Typography className={classes.adTypesTitle}>{text(v)}</Typography>

                          <Box className={classes.adTypesPlatforms}>
                            <AdTypePlatforms adTypes={[v as AdTypeFromQuery]} size={20} />
                          </Box>
                        </Box>
                      </Box>
                    </RadioRow>
                    {selectedAdType(v) && (
                      <Box
                        className={clsx(classes.text, classes.adTypesSettingsLink)}
                        onClick={() => handleShowModal(v)}
                      >
                        <TuneIcon
                          className={classes.adTypesSettingsLinkIcon}
                          htmlColor='#fff'
                          fontSize='small'
                        />

                        <Trans>Настроить</Trans>
                      </Box>
                    )}
                  </Box>
                  {roles.includes('REVIEWER') && (
                    <RadioRow
                      selected={selectedMonitorAdType(v)}
                      mt={3}
                      onSelect={handleSelectMonitorAdType(v)}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  {sensorsModalsDispay[v] && (
                    <SensorsModal
                      sensors={data.currentUser?.sensors}
                      selectedSensors={adTypeSelectedSensors[v]}
                      adType={v}
                      onClose={() => handleCloseModal(v)}
                      onSubmit={(s: unknown) => handleSubmit(v, s as Record<string, boolean>)}
                    />
                  )}
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  const renderButtons = () => {
    const selectedSensorNames = () => {
      const result: Mutable<AccountsAdTypeSensorsInput> = {
        context: [],
        target: [],
      };

      R.pipe(
        R.toPairs,
        R.forEach(([adType, values]) => {
          const isAllSelected = R.all(v => v[1], R.toPairs(values));
          let sensorNames: string[] = [];

          const toPairsSB = R.toPairs as (o: Record<string, boolean>) => Array<[string, boolean]>;

          if (!isAllSelected) {
            sensorNames = R.pipe(
              toPairsSB,
              R.filter((v: [string, boolean]) => v[1]),
              R.map(v => v[0]),
            )(values);
          }
          const key = adType.toLowerCase() as keyof Mutable<AccountsAdTypeSensorsInput>;
          result[key] = sensorNames;
        }),
      )(adTypeSelectedSensors);

      return result;
    };

    // In isNewFlow the ad-types radios are hidden — `adTypes` is still required
    // by the input schema, so fall back to all available ad types. Reviewer
    // monitorAdTypes are also hidden in this mode, so submit an empty list.
    const effectiveAdTypes = isNewFlow ? [...AD_TYPES] : adTypesState;
    const effectiveMonitorAdTypes = isNewFlow ? [] : monitorAdTypesState;

    const handleSave = async () => {
      try {
        if (notification && id) {
          const updateInput: AccountsNotificationUpdateInput = {
            id,
            adTypes: effectiveAdTypes,
            emailChannel: {email},
            adTypeSensors: selectedSensorNames(),
            monitorAdTypes: effectiveMonitorAdTypes,
            recommendations: [],
            ...(groupsModeActive ? {selectedSensorGroups: selectedGroups} : {}),
          };
          await updateNotificationNew({variables: {input: updateInput}});
        } else if (specialistId) {
          const createInput: AccountsNotificationCreateInput = {
            specialistId,
            companyId,
            channelType,
            adTypes: effectiveAdTypes,
            emailChannel: {email},
            telegramChannel: {xeniooUserUuid: xeniooUserUuid()},
            adTypeSensors: selectedSensorNames(),
            monitorAdTypes: effectiveMonitorAdTypes,
            recommendations: [],
            ...(groupsModeActive ? {selectedSensorGroups: selectedGroups} : {}),
          };
          await createNotificationNew({variables: {input: createInput}});
        }

        navigate({
          pathname: '/home/settings/notifications',
          search: `?specialistId=${specialistId}`,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };

    const handleDelete = async () => {
      if (id) {
        await deleteNotification({variables: {id}});
        setDeleted(true);
      }
    };

    const hasChannelValue = channelType === 'TELEGRAM' || (channelType === 'EMAIL' && email);
    const stateValid = isNewFlow
      ? selectedGroups.length > 0 && hasChannelValue
      : (adTypesState.length > 0 || monitorAdTypesState.length > 0) && hasChannelValue;

    return (
      <Grid container>
        <Grid item xs={12}>
          <PrimaryMediumButton
            className={classes.actionButton}
            disabled={createNotificationNewLoading || updateNotificationNewLoading || !stateValid}
            data-testid='save-button'
            onClick={handleSave}
          >
            <SaveOutlinedIcon
              className={classes.actionButtonIcon}
              fontSize='small'
              htmlColor='#FFFFFF'
            />

            <Trans>Сохранить</Trans>
          </PrimaryMediumButton>
        </Grid>

        {notification && (
          <Grid item xs={12}>
            <Box mt={4}>
              <SecondaryMediumButton
                className={classes.actionButton}
                disabled={deleteNotificationLoading}
                onClick={handleDelete}
              >
                <Trans>Удалить</Trans>
              </SecondaryMediumButton>
            </Box>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderTelegramBotLink = () => {
    if (channelType !== 'TELEGRAM') return null;

    const botLink = `http://t.me/adsensor_notifications_bot?start=${xeniooUserUuid()}`;

    return (
      <Box
        className={classes.row}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        flexWrap={'wrap'}
      >
        <Box display='flex' flexDirection='column'>
          <Box>
            <Typography className={classes.tgBotTitle}>
              <Trans>Телеграм бот</Trans>
            </Typography>
          </Box>
          <Box mt={2}>
            <Typography className={classes.hintNew}>
              <Trans>
                Вы можете скопировать и отправить эту ссылку специалисту, который будет получать
                уведомления
              </Trans>
            </Typography>
          </Box>
        </Box>
        <Link className={classes.linkNew} href={botLink} target='_blank'>
          <Trans>Подключить бота</Trans>
        </Link>
      </Box>
    );
  };

  const breadcrumbs = [
    {
      href: '/home/settings',
      title: <Trans>Настройки</Trans>,
    },
    {
      href: '/home/settings/specialists',
      title: <Trans>Уведомления</Trans>,
    },
    {
      href: '',
      title: <Trans>Управление</Trans>,
    },
  ];

  return (
    <>
      <Box mb={10}>
        <Breadcrumbs items={breadcrumbs} />
      </Box>

      <Box mt={8} />
      <Typography className={classes.title}>
        <Trans>Настройка уведомления</Trans>
      </Typography>

      <Box display='flex' flexWrap='wrap'>
        {renderChannelTypeSelect()}
        {renderCompanySelect()}
      </Box>

      {renderTelegramBotLink()}

      <Box mt={6}>{renderAdTypeRadios()}</Box>

      <Box mt={6}>{renderButtons()}</Box>
    </>
  );
};
